import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Package,
  Users,
  Building2,
  Layers,
  Check,
  X,
  Euro,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const TierManagementPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);
  const [modules, setModules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tierToDelete, setTierToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    billing_cycle: 'monthly',
    modules: [],
    features: [],
    max_users: null,
    max_leads_per_month: null,
    target_type: 'customer',
    is_active: true,
    sort_order: 0
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, modulesRes] = await Promise.all([
        api.get('/admin/tiers?include_inactive=true'),
        api.get('/admin/tiers/modules')
      ]);
      setTiers(tiersRes.data.tiers || []);
      setModules(modulesRes.data.modules || []);
    } catch (error) {
      toast.error('Kon tiers niet laden');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingTier(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      billing_cycle: 'monthly',
      modules: [],
      features: [],
      max_users: null,
      max_leads_per_month: null,
      target_type: 'customer',
      is_active: true,
      sort_order: tiers.length
    });
    setDialogOpen(true);
  };

  const openEditDialog = (tier) => {
    setEditingTier(tier);
    setFormData({
      name: tier.name || '',
      description: tier.description || '',
      price: tier.price || 0,
      billing_cycle: tier.billing_cycle || 'monthly',
      modules: tier.modules || [],
      features: tier.features || [],
      max_users: tier.max_users,
      max_leads_per_month: tier.max_leads_per_month,
      target_type: tier.target_type || 'customer',
      is_active: tier.is_active !== false,
      sort_order: tier.sort_order || 0
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Naam is verplicht');
      return;
    }

    setSaving(true);
    try {
      if (editingTier) {
        await api.put(`/admin/tiers/${editingTier.id}`, formData);
        toast.success('Tier bijgewerkt');
      } else {
        await api.post('/admin/tiers', formData);
        toast.success('Tier aangemaakt');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!tierToDelete) return;
    
    try {
      const response = await api.delete(`/admin/tiers/${tierToDelete.id}`);
      toast.success(response.data.message || 'Tier verwijderd');
      setDeleteDialogOpen(false);
      setTierToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Verwijderen mislukt'));
    }
  };

  const toggleModule = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleId)
        ? prev.modules.filter(m => m !== moduleId)
        : [...prev.modules, moduleId]
    }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
              Configuratie
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-black dark:text-white" data-testid="tier-management-title">
              Abonnement Tiers
            </h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              Beheer abonnementstiers voor klanten en leveranciers
            </p>
          </div>
          <Button onClick={openCreateDialog} data-testid="create-tier-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nieuwe Tier
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Layers className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black dark:text-white">{tiers.length}</p>
                  <p className="text-xs text-neutral-500">Totaal tiers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black dark:text-white">
                    {tiers.filter(t => t.target_type === 'customer' || t.target_type === 'both').length}
                  </p>
                  <p className="text-xs text-neutral-500">Klant tiers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black dark:text-white">
                    {tiers.filter(t => t.target_type === 'supplier' || t.target_type === 'both').length}
                  </p>
                  <p className="text-xs text-neutral-500">Leverancier tiers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tiers Table */}
        <Card className="border-neutral-200 dark:border-neutral-800">
          <CardHeader>
            <CardTitle className="text-base">Alle Tiers</CardTitle>
            <CardDescription>Overzicht van alle abonnementstiers</CardDescription>
          </CardHeader>
          <CardContent>
            {tiers.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Naam</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Prijs</TableHead>
                    <TableHead>Cyclus</TableHead>
                    <TableHead>Modules</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiers.map((tier) => (
                    <TableRow key={tier.id} data-testid={`tier-row-${tier.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-black dark:text-white">{tier.name}</p>
                          {tier.description && (
                            <p className="text-xs text-neutral-500 truncate max-w-[200px]">
                              {tier.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          tier.target_type === 'customer' ? 'default' :
                          tier.target_type === 'supplier' ? 'secondary' : 'outline'
                        }>
                          {tier.target_type === 'customer' ? 'Klant' :
                           tier.target_type === 'supplier' ? 'Leverancier' : 'Beide'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Euro className="h-3 w-3 text-neutral-400" />
                          <span className="font-medium">{tier.price?.toFixed(2)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-neutral-600">
                          <Calendar className="h-3 w-3" />
                          {tier.billing_cycle === 'monthly' ? 'Maandelijks' : 'Jaarlijks'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {tier.modules?.slice(0, 3).map(moduleId => {
                            const mod = modules.find(m => m.id === moduleId);
                            return mod ? (
                              <Badge key={moduleId} variant="outline" className="text-xs">
                                {mod.name}
                              </Badge>
                            ) : null;
                          })}
                          {tier.modules?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{tier.modules.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {tier.is_active ? (
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            <Check className="h-3 w-3 mr-1" />
                            Actief
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <X className="h-3 w-3 mr-1" />
                            Inactief
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(tier)}
                            data-testid={`edit-tier-${tier.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setTierToDelete(tier);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-tier-${tier.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                <p className="text-neutral-500 mb-4">Nog geen tiers aangemaakt</p>
                <Button onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Eerste Tier Aanmaken
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTier ? 'Tier Bewerken' : 'Nieuwe Tier Aanmaken'}
              </DialogTitle>
              <DialogDescription>
                Configureer de eigenschappen van deze abonnementstier
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Naam *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Bijv. Basis, Pro, Enterprise"
                    data-testid="tier-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Doelgroep</Label>
                  <Select
                    value={formData.target_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, target_type: value }))}
                  >
                    <SelectTrigger data-testid="tier-target-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Klanten</SelectItem>
                      <SelectItem value="supplier">Leveranciers</SelectItem>
                      <SelectItem value="both">Beide</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Beschrijving</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Korte beschrijving van deze tier..."
                  rows={2}
                />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prijs (EUR)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    data-testid="tier-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Factuurcyclus</Label>
                  <Select
                    value={formData.billing_cycle}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, billing_cycle: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Maandelijks</SelectItem>
                      <SelectItem value="yearly">Jaarlijks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max. gebruikers (leeg = onbeperkt)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_users || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      max_users: e.target.value ? parseInt(e.target.value) : null 
                    }))}
                    placeholder="Onbeperkt"
                  />
                </div>
                {(formData.target_type === 'supplier' || formData.target_type === 'both') && (
                  <div className="space-y-2">
                    <Label>Max. leads/maand (leeg = onbeperkt)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.max_leads_per_month || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        max_leads_per_month: e.target.value ? parseInt(e.target.value) : null 
                      }))}
                      placeholder="Onbeperkt"
                    />
                  </div>
                )}
              </div>

              {/* Modules */}
              <div className="space-y-3">
                <Label>Inbegrepen Modules</Label>
                <div className="grid grid-cols-2 gap-2">
                  {modules.map((module) => (
                    <div
                      key={module.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.modules.includes(module.id)
                          ? 'border-black bg-neutral-50 dark:border-white dark:bg-neutral-800'
                          : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                      }`}
                      onClick={() => toggleModule(module.id)}
                    >
                      <Checkbox
                        checked={formData.modules.includes(module.id)}
                        onCheckedChange={() => toggleModule(module.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-black dark:text-white">{module.name}</p>
                        <p className="text-xs text-neutral-500 truncate">{module.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3">
                <Label>Extra Features</Label>
                <div className="flex gap-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Bijv. 24/7 support, Dedicated account manager..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" variant="outline" onClick={addFeature}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.features.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.features.map((feature, index) => (
                      <Badge key={index} variant="secondary" className="pl-2 pr-1 py-1">
                        {feature}
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="ml-1 hover:bg-neutral-300 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <div>
                  <p className="font-medium text-black dark:text-white">Tier Actief</p>
                  <p className="text-sm text-neutral-500">Inactieve tiers zijn niet zichtbaar voor gebruikers</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annuleren
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-tier-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingTier ? 'Opslaan' : 'Aanmaken'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Tier Verwijderen</DialogTitle>
              <DialogDescription>
                Weet u zeker dat u de tier "{tierToDelete?.name}" wilt verwijderen?
                {tierToDelete?.is_active && (
                  <span className="block mt-2 text-amber-600">
                    Let op: Als organisaties deze tier gebruiken, wordt de tier gedeactiveerd in plaats van verwijderd.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Annuleren
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Verwijderen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default TierManagementPage;
